/**
 * Schema
 */
import { promises as fs } from 'fs';
import { inspect } from 'util';
import { initAJV } from '../ajv/init';
import AJV, { ErrorObject, ValidateFunction } from '../ajv/module';
import { ISchemaAJV } from '../ajv/types';
import { stringify } from '../context';
import { JSON_SCHEMA_VERSION } from '../globals';
import { determinePropertyPathFromId } from './determinePropertyPathFromId';
import { ExtractTSTypeFromType } from './extract';
import { formatDataBeforeValidation, reformatDataAfterValidation } from './format/data';
import { removeRefs } from './format/removeRefs';
import { formatSchemaForAJV } from './format/schemaToAJV';
import { ISchemaObject, RootSchema } from './properties';
import { ISchemaNull, SchemaTypeName } from './properties/primitives';
import { IGraphQLObjectMode, toGraphQL } from './toGraphQL';
import { toTypescript } from './toTypescript';
import { ISchemaInputSchemaType } from './types';

/**  Forwarded exports  */
export * from './extract';
export * from './format/schemaToAJV';
export * from './properties';
export * from './properties/primitives';
export * from './types';

/**
 * @todo: Which of the above two types should serve as the base generic?
 * - i.e.: Do we REQUIRE "$id" & "title" at the top-level?
 * - In an EntitySchema case we do, but not here!
 */

/**  Types: General  */
export type ISchemaInstance<T extends ISchemaInputSchemaType = ISchemaInputSchemaType> = Schema<T>;
export type ISchemaDependencies = Array<Schema<ISchemaInputSchemaType>>;

//  Constructor interface
export interface ISchemaConstructor<InputSchema extends ISchemaInputSchemaType> {
    schema: InputSchema;
    dependencies: ISchemaDependencies;

    //  Removes additional properties from .validate()'s response.output
    //  --> Note that this does _not_ affect the input object
    prune_additional_properties?: boolean;

    ajv?: ISchemaAJV;
}

//  .validate()
export type ISchemaValidateResponse<TSType> =
    | {
          is_valid: true;
          output: TSType;
          error: null;
          error_objects: null;
      }
    | {
          is_valid: false;
          output: null;
          error: string;
          error_objects: Array<ErrorObject>;
      };

export type IErrorObject = ErrorObject;

/**
 * Export class
 */
export class Schema<
    InputSchema extends ISchemaInputSchemaType,
    TSType = ExtractTSTypeFromType<InputSchema>
> {
    /**  AJV  */
    readonly ajv: AJV;
    readonly _validate: ValidateFunction<TSType>;

    /**  Schema  */
    readonly schema: Readonly<InputSchema>;
    readonly dependencies: ISchemaDependencies;
    readonly title?: string;

    /**  Export types  */

    // @ts-expect-error: Use `typeof Schema["__object_type"]` to get type of `InputObject`
    readonly __object_type: TSType;

    /**
     * Constructor
     */
    constructor({
        schema,
        dependencies: input_dependencies,
        prune_additional_properties = false,
        ajv: input_ajv,
    }: ISchemaConstructor<InputSchema>) {
        try {
            //  Build schema
            this.schema = buildSchema(schema);

            //  Set title
            this.title = this.schema.title;

            //  Init ajv, conditionally
            this.ajv =
                (input_ajv?.is_instantiated && input_ajv?.ajv) ||
                initAJV({ prune_additional_properties }).ajv;

            //  Extract dependent schemata, and add them to AJV instance
            this.dependencies = validateAndFlattenDependencies(input_dependencies);
            if (this.dependencies.length && !this.schema.$id) {
                throw new Error(`Schemata that use dependencies must define an "$id" of their own`);
            }

            //  Add schemata to AJV instance and compile
            this._validate = this.compileSchema();

            //  Throw if compile() fails
            if (this.ajv.errors) {
                throw this.ajv.errors;
            }
        } catch (error) {
            console.error(error);
            console.error(`schema:`, inspect(schema, { depth: null }));

            throw new Error(`Schema/constructor: ${error}`);
        }
    }

    /**
     * Compile the schema for AJV validation
     */
    compileSchema(): ValidateFunction<TSType> {
        if (this._validate) {
            return this._validate;
        }

        /**
         * Compile dependencies on AJV instance
         */
        Array.from(
            new Set<Readonly<ISchemaInputSchemaType>>(this.dependencies.map(d => d.schema))
        ).forEach(schema =>
            compileAjvSchema({
                ajv: this.ajv,
                schema,
            })
        );

        /**
         * Compile *this* AJV schema
         */
        return compileAjvSchema({
            ajv: this.ajv,
            schema: this.schema,
        });
    }

    /**
     * Validate an object against the schema
     */
    validate(input_data: TSType): ISchemaValidateResponse<TSType> {
        try {
            //  this._validate() will modify the `data` arg
            //  - therefore, we clone first; don't want to modify input
            const data = formatDataBeforeValidation(input_data);

            const is_valid = this._validate(data);
            const validation_errors = this._validate.errors || [];

            return is_valid
                ? {
                      is_valid: true,
                      output: reformatDataAfterValidation(data),
                      error: null,
                      error_objects: null,
                  }
                : {
                      is_valid: false,
                      output: null,
                      error: stringify(validation_errors),
                      error_objects: validation_errors,
                  };
        } catch (error) {
            throw new Error(`Schema/validate: ${error}`);
        }
    }

    /**
     * Convert schema to JSONSchema JS object
     */
    toJSONSchemaObject({ refs }: { refs: 'MAINTAIN' | 'RESOLVE' }): Readonly<InputSchema> {
        return refs === 'MAINTAIN' ? this.schema : this.toReflessSchema();
    }

    /**
     * Stringify schema
     */
    toJSONSchemaString({ refs }: { refs: 'MAINTAIN' | 'RESOLVE' }): string {
        return JSON.stringify(this.toJSONSchemaObject({ refs }));
    }

    /**
     * Save schema in file
     */
    async toFile({
        output_path,
        refs,
    }: {
        output_path: string;
        refs: 'MAINTAIN' | 'RESOLVE';
    }): Promise<void> {
        try {
            await fs.writeFile(output_path, this.toJSONSchemaString({ refs }));
        } catch (error) {
            throw new Error(`Schema/toFile: ${error}`);
        }
    }

    /**
     * Convert schema to a Typescript schema
     */
    toTypescript(): string {
        /**
         * Only valid if we have .title
         */
        if (!this.title) {
            throw new Error(
                `Schema/toTypescript: .toTypescript() can only be called when title & $id are provided`
            );
        }

        /**
         * Only valid if input schema is an object schema
         */
        const object_schema = this.schema as RootSchema<ISchemaObject<any, string>>;

        if (object_schema.type !== SchemaTypeName.object) {
            throw new Error(
                `Schema/toTypescript: .toTypescript() can only be called when schema.type is "${SchemaTypeName.object}"`
            );
        }

        try {
            return toTypescript({
                name: this.title,
                schema: object_schema,
            });
        } catch (error) {
            console.error('Schema/toTypescript', this.schema);
            console.error('Schema/toTypescript', error);
            throw new Error(`Schema/toTypescript: ${error}`);
        }
    }

    /**
     * Convert schema to a GraphQL schema
     */
    toGraphQL({ mode }: { mode: IGraphQLObjectMode }): string {
        /**
         * Only valid if we have .title
         */
        if (!this.title) {
            throw new Error(
                `Schema/toGraphQL: .toGraphQL() can only be called when title & $id are provided`
            );
        }

        /**
         * Only valid if input schema is an object schema
         */
        const object_schema = this.schema as RootSchema<ISchemaObject<any, string>>;

        if (object_schema.type !== SchemaTypeName.object) {
            throw new Error(
                `Schema/toGraphQL: .toGraphQL() can only be called when schema.type is "${SchemaTypeName.object}"`
            );
        }

        try {
            return toGraphQL({
                name: this.title,
                schema: object_schema,
                mode,
            });
        } catch (error) {
            console.error('Schema/toGraphQL', this.schema);
            console.error('Schema/toGraphQL', error);
            throw new Error(`Schema/toGraphQL: ${error}`);
        }
    }

    /**
     * Remove $ref instances from a schema
     * - Useful for schema serialization, where the serialized schema will be rehydrated in a Schema instance
     */
    toReflessSchema(): InputSchema {
        return removeRefs(this.schema as InputSchema);
    }
}

/**
 * Compile schema with AJV
 */
function compileAjvSchema<TSType = any>({
    ajv,
    schema,
}: {
    ajv: AJV;
    schema: Readonly<ISchemaInputSchemaType>;
}): ValidateFunction<TSType> {
    //  Compile schema
    const validate = ajv.compile<TSType>(schema);

    if (ajv.errors) {
        throw ajv.errors;
    }

    return validate;
}

/**
 * Build schema from input schema
 */
function buildSchema<InputSchema extends ISchemaInputSchemaType>(
    input_schema: InputSchema
): InputSchema {
    /**
     * For schema-to-schema $ref support, extract the path from the base URI
     * - e.g. http://example.com/schema/Review --> Review
     */
    const property_path_prefix = determinePropertyPathFromId({ $id: input_schema.$id });

    /**
     * Assemble schema object
     */
    const schema = {
        //  Schema
        ...input_schema,

        //  Schema defaults
        $schema: JSON_SCHEMA_VERSION,
    };

    //  If input schema is null schema, delete `default` value: `default` is not supported in root schema!
    const null_schema = schema as RootSchema<ISchemaNull>;
    if (null_schema.type === SchemaTypeName.null) {
        delete null_schema.default;

        //  Deletion of .default is propagated to var `schema`; same object reference
        return formatSchemaForAJV({ schema, ref_path: property_path_prefix });
    }

    return formatSchemaForAJV({ schema, ref_path: property_path_prefix });
}

/**
 * Flatten input schema dependencies and their antecedents
 */
function validateAndFlattenDependencies(
    input_dependencies: ISchemaDependencies
): ISchemaDependencies {
    /**
     * 1: Flatten dependencies into Set()
     */
    const dependencies = new Set<ISchemaDependencies[number]>();

    input_dependencies.forEach(s => {
        //  Add the dependencies of the dependency
        s.dependencies?.forEach(d => {
            dependencies.add(d);
        });

        //  Add the dependency itself
        dependencies.add(s);
    });

    /**
     * 2: Validate them!
     * - Dependencies must be RootSchemata: $id required for $refs
     */
    return Array.from(dependencies).map(dependency => {
        if (!dependency.schema.$id) {
            throw new Error(
                `Schema/validateAndFlattenDependencies: $id is required on all schemata in dependencies[]`
            );
        }

        return dependency;
    });
}
